import React, { useState } from 'react';
import EmailModal from './EmailModal';
import SupplierDatabaseModal from './SupplierDatabaseModal';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { API_ENDPOINTS } from '../utils/config';
import './SupplierActionButton.css';

interface SupplierActionButtonProps {
  requestId: string;
  supplier: any;
  supplierIndex: number;
  requestData?: any; // Procurement request data
  onActionCompleted: (supplierIndex?: number) => void;
}

const SupplierActionButton: React.FC<SupplierActionButtonProps> = ({
  requestId,
  supplier,
  supplierIndex,
  requestData,
  onActionCompleted
}) => {
  const { t } = useTranslation();
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isSupplierDbModalOpen, setIsSupplierDbModalOpen] = useState(false);
  const [isMarkingEmailReceived, setIsMarkingEmailReceived] = useState(false);

  const { token } = useAuth();

  const handleSendEmail = () => {
    setIsEmailModalOpen(true);
  };

  const handleEmailSent = () => {
    onActionCompleted(supplierIndex);
  };

  const handleAddToDatabase = () => {
    setIsSupplierDbModalOpen(true);
  };

  const handleMarkEmailReceived = async () => {
    if (!token) return;
    
    setIsMarkingEmailReceived(true);
    try {
      const response = await fetch(API_ENDPOINTS.MARK_EMAIL_RECEIVED(requestId, supplierIndex.toString()), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Update the supplier's email_received status locally
        supplier.email_received = true;
        supplier.email_received_at = new Date().toISOString();
        
        if (result.phase_updated) {
          // Phase was updated, refresh the request data
          onActionCompleted(supplierIndex);
        }
      } else {
        console.error('Failed to mark email as received');
      }
    } catch (error) {
      console.error('Error marking email as received:', error);
    } finally {
      setIsMarkingEmailReceived(false);
    }
  };

  return (
    <>
      <div className="supplier-actions">
        <div className="action-buttons">
          <button
            className={`email-btn ${supplier.email_sent ? 'sent' : ''}`}
            onClick={handleSendEmail}
            disabled={supplier.email_sent}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            {supplier.email_sent ? t('common.email_sent') : t('common.send_email')}
          </button>
          
          <button
            className={`database-btn ${supplier.added_to_db ? 'added' : ''}`}
            onClick={handleAddToDatabase}
            disabled={supplier.added_to_db}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10,9 9,9 8,9"/>
            </svg>
            {supplier.added_to_db ? t('common.added_to_db') : t('common.add_to_database')}
          </button>

          {/* Email Received Checkbox - Always show */}
          <div className="email-received-section">
            <label className="email-received-checkbox">
              <input
                type="checkbox"
                checked={supplier.email_received || false}
                onChange={handleMarkEmailReceived}
                disabled={isMarkingEmailReceived || supplier.email_received}
              />
              <span className="checkmark"></span>
              {isMarkingEmailReceived ? t('common.marking_received') : t('common.email_received')}
            </label>
          </div>
        </div>
        
        {/* Status indicators */}
        <div className="status-indicators">
          {supplier.email_sent && (
            <span className="status-badge email-sent">
              {t('common.email_sent_at', { date: new Date(supplier.email_sent_at).toLocaleDateString() })}
            </span>
          )}
          {supplier.added_to_db && (
            <span className="status-badge database-added">
              {t('common.added_to_db_at', { date: new Date(supplier.added_to_db_at).toLocaleDateString() })}
            </span>
          )}
        </div>
      </div>

      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        requestId={requestId}
        supplierIndex={supplierIndex}
        supplierName={supplier.company_name}
        supplierEmail={supplier.email}
        onEmailSent={handleEmailSent}
        requestData={requestData}
      />
      
      <SupplierDatabaseModal
        isOpen={isSupplierDbModalOpen}
        onClose={() => setIsSupplierDbModalOpen(false)}
        initialSupplierData={{
          ...supplier,
          // Add procurement request data for product pre-filling
          product_name: requestData?.product_name,
          product_brand: requestData?.product_brand,
          product_global_code: requestData?.product_global_code,
          product_company_code: requestData?.product_company_code,
          product_description: requestData?.product_description,
          product_category: requestData?.product_category,
          price: requestData?.price,
          budget: requestData?.budget,
          currency: requestData?.currency,
          delivery_time: requestData?.delivery_time,
          additional_specifications: requestData?.additional_specifications,
          custom_specifications: requestData?.custom_specifications
        }}
        onSupplierAdded={(supplierIndex) => onActionCompleted(supplierIndex)}
        requestId={requestId}
        supplierIndex={supplierIndex}
      />
    </>
  );
};

export default SupplierActionButton; 